import React, { FC, PropsWithChildren, useMemo } from 'react';
import {
  Button,
  DatePickerInput,
  SelectInput,
  ISelectOption,
  RichInput,
  InputChangeEvent,
  CurrencyInput,
} from '@studykik/ui-library';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { formatValue } from 'react-currency-input-field';
import { usePaymentSubmissionsContext } from '../../contexts/payment-submissions-context';
import { ExpenseCategory, ExpenseFormData } from '../../models/paymentConfig';
import { AddReceiptField } from './add-receipt-field';
import {
  PaymenExpenseButtonContainer,
  PaymentSubmissionFieldContainer,
  PaymenSubmissionFieldLabel,
  PaymenSubmissionFieldWrapper,
  PaymentSubmissionExpenseForm,
} from './styles';
import { getLocaleByCurrencyCode } from '../../utils/formatCurrency';
import './styles.scss';

type AddExpenseFormProps = PropsWithChildren & {
  expenseCategories: ExpenseCategory[];
};

export const handleChangeForAmount = (e: InputChangeEvent) => {
  const inputValue = e.target.value;

  if (!Number.isNaN(parseFloat(inputValue))) {
    return parseFloat(inputValue);
  }

  return null;
};

const AddExpenseFormComponent: FC<AddExpenseFormProps> = ({
  expenseCategories,
}) => {
  const { addExpense, selectedPaymentSubcategory } =
    usePaymentSubmissionsContext();

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { touchedFields },
  } = useForm<ExpenseFormData>({
    defaultValues: {
      date: '',
      amount: 0,
    },
  });

  const { t } = useTranslation();

  const navigate = useNavigate();

  const goBack = () => navigate('/payment/submission');

  const onSubmit = (formData: ExpenseFormData) => {
    const b64ImgData = formData.base64ImageData
      ? formData.base64ImageData.split(';')[0].split(':')[1]
      : '';

    addExpense({
      ...formData,
      base64ImageData: formData.base64ImageData
        ? formData.base64ImageData.split(';base64,')[1]
        : undefined,
      imageMimeType: b64ImgData || undefined,
      imageExtension: b64ImgData ? `${b64ImgData.split('/')[1]}` : undefined,
      expenseCategoryName: expenseCategories.find(
        ({ id }) => formData.expenseCategoryId === id
      )?.name,
    });

    goBack();
  };

  const expensesOptions = useMemo(
    () => expenseCategories.map(({ id, name }) => ({ label: name, value: id })),
    [expenseCategories]
  );

  const onCancel = () => {
    reset();
    goBack();
  };

  const isValid =
    touchedFields.amount &&
    touchedFields.expenseCategoryId &&
    getValues().amount > 0 &&
    getValues().date &&
    getValues().expenseCategoryId;

  return !selectedPaymentSubcategory ? null : (
    <PaymentSubmissionExpenseForm onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="base64ImageData"
        control={control}
        rules={{ required: false }}
        render={({ field }) => (
          <AddReceiptField value={field.value} onChange={field.onChange} />
        )}
      />

      <PaymentSubmissionFieldContainer>
        <PaymenSubmissionFieldLabel required>
          {t('Category')}
        </PaymenSubmissionFieldLabel>
        <PaymenSubmissionFieldWrapper>
          <Controller
            name="expenseCategoryId"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <SelectInput
                selected={field.value}
                onSelect={(v) => {
                  field.onChange((v as unknown as ISelectOption).value);
                  field.onBlur();
                }}
                options={expensesOptions}
                includeAll={false}
                hideSearch
              />
            )}
          />
        </PaymenSubmissionFieldWrapper>
      </PaymentSubmissionFieldContainer>

      <PaymentSubmissionFieldContainer>
        <PaymenSubmissionFieldLabel required>
          {t('Amount')}
        </PaymenSubmissionFieldLabel>
        <PaymenSubmissionFieldWrapper>
          <Controller
            name="amount"
            control={control}
            rules={{ required: true }}
            render={({ field }) => {
              const intlConfig = {
                locale: getLocaleByCurrencyCode(
                  selectedPaymentSubcategory.currency
                ),
                currency: selectedPaymentSubcategory.currency,
              };

              return (
                <CurrencyInput
                  className="currency-input"
                  placeholder={formatValue({ value: '0', intlConfig })}
                  intlConfig={intlConfig}
                  onValueChange={(_value, _name, values) => {
                    field.onChange(parseFloat(values?.value.replace(',', '.') || '') || 0);
                    field.onBlur();
                  }}
                  disableGroupSeparators
                />
              );
            }}
          />
        </PaymenSubmissionFieldWrapper>
      </PaymentSubmissionFieldContainer>

      <PaymentSubmissionFieldContainer>
        <PaymenSubmissionFieldLabel required>
          {t('Date')}
        </PaymenSubmissionFieldLabel>
        <PaymenSubmissionFieldWrapper>
          <Controller
            name="date"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <DatePickerInput
                value={field.value}
                maxDate={new Date()}
                onChange={(value) => {
                  field.onChange(value);
                  field.onBlur();
                }}
              />
            )}
          />
        </PaymenSubmissionFieldWrapper>
      </PaymentSubmissionFieldContainer>

      <PaymentSubmissionFieldContainer>
        <PaymenSubmissionFieldLabel>
          {t('Notes')}
          <span>({t('optional')})</span>
        </PaymenSubmissionFieldLabel>
        <PaymenSubmissionFieldWrapper>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <RichInput
                alwaysExpanded
                maxChars={250}
                hideStyleControls
                hideCancelButton
                hideSubmitButton
                submitPlainText
                onSubmit={() => new Promise(() => {})}
                onChange={(value) => {
                  field.onChange(value);
                  field.onBlur();
                }}
                placeholder={t('Enter some text...')}
              />
            )}
          />
        </PaymenSubmissionFieldWrapper>
      </PaymentSubmissionFieldContainer>
      <PaymenExpenseButtonContainer>
        <Button onClick={onCancel} variant="outlined">
          {t('Cancel')}
        </Button>
        <Button type="submit" disabled={!isValid}>
          {t('Submit')}
        </Button>
      </PaymenExpenseButtonContainer>
    </PaymentSubmissionExpenseForm>
  );
};

export default AddExpenseFormComponent;
